import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { AlertTriangle, CalendarDays, RefreshCw, X } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useOrganizationTimeZone } from '@/lib/org-date-locale'
import { cn } from '@/lib/utils'
import { jobsApi } from '@/services/jobs'
import { useAuthStore } from '@/stores/auth-store'
import type { Job as ApiJob } from '@/types'
import { toast } from 'sonner'

import { buildPendingJob, createApiRealtimeSource } from '../api/realtime-api'
import { createMockRealtimeSource, MOCK_TIME_ZONE } from '../api/mock-realtime'
import { realtimeConfig } from '../config'
import { useJobWorkerActions } from '../hooks/use-job-worker-actions'
import { useHighlightedJobs, useJobs, useWorkers } from '../hooks/use-entities'
import { useMediaQuery, useNow, usePrefersReducedMotion } from '../hooks/use-environment'
import { useNearbyCandidates } from '../hooks/use-nearby-candidates'
import { useRealtimeSocket } from '../hooks/use-realtime-socket'
import { useRealtimeStore } from '../store/realtime-store'
import type { Job, JobsTab, KpiFilter, MapLayer, Worker } from '../types'
import {
  computeFitPadding,
  getEffectiveResponse,
  getRemovalRule,
  hasPostPermission,
  MAX_NEARBY_CANDIDATES,
  NEARBY_MAX_RADIUS_KM,
  NEARBY_RADIUS_KM,
  NEARBY_RADIUS_STEP_KM,
  schedulingRules,
  type RemovalReason,
} from '../schedulingRules'
import { hasValidCoords, toValidCoords } from '../utils/distance'
import { buildJobLists } from '../utils/job-lists'
import { findWorkerConflicts, getActiveJobWorkers, isActiveResponse, isJobUnderstaffed, jobHasWorker } from '../utils/job-workers'
import {
  applyKpiFilter,
  computeKpis,
  getJobDisplayStatus,
  isJobOnDate,
  type JobDisplayStatus,
} from '../utils/job-status'
import { countNearbyCandidates, type NearbyCandidate } from '../utils/nearby-workers'
import { formatDateKeyLabel, formatTimeInZone, formatTimeRangeInZone, getTimeZoneAbbreviation, toDateKey } from '../utils/time'
import '../realtime.css'
import { JobCard } from './JobCard'
import { JobsPanel } from './JobsPanel'
import { KpiCards } from './KpiCards'
import { LiveScheduleTimeline } from './LiveScheduleTimeline'
import { LiveStatusPill } from './LiveStatusPill'
import { MapJobPopup } from './MapJobPopup'
import { MobileJobsSheet, type SheetSnap } from './MobileJobsSheet'
import { NearbyWorkersStrip } from './NearbyWorkersStrip'
import { RemoveWorkerDialog, type RemoveWorkerRequest } from './RemoveWorkerDialog'
import { StatusChip } from './status-meta'
import { WorkersMap } from './WorkersMap'

const KPI_TO_TAB: Record<KpiFilter, JobsTab> = { on_job: 'today', available: 'unassigned', unassigned: 'unassigned', late: 'late' }
const KPI_LABEL: Record<KpiFilter, string> = { on_job: 'Workers on job', available: 'Available now', unassigned: 'Jobs unassigned', late: 'Running late' }
const WORKER_ORDER: Record<Worker['status'], number> = { on_job: 0, late: 1, available: 2, offline: 3 }

/** Re-measure rows while dragging so drops stay accurate after the page scrolls. */
const DND_MEASURING = { droppable: { strategy: MeasuringStrategy.Always } }

/** Drop on the row under the pointer; keyboard dragging (no pointer) falls back to overlap. */
const collisionDetection: CollisionDetection = (args) => {
  const underPointer = pointerWithin(args)
  return underPointer.length > 0 ? underPointer : rectIntersection(args)
}

/** An add that needs confirming (drag-and-drop, or a nearby worker with a schedule conflict). */
interface PendingDrop {
  job: Job
  worker: Worker
  conflicts: Job[]
  hasConflict: boolean
}

/** Reuses display objects whose content did not change, so memoized cards skip re-rendering. */
function useStableDisplayMap(jobs: Job[], now: number, timeZone: string) {
  const cacheRef = useRef(new Map<string, JobDisplayStatus>())
  return useMemo(() => {
    const next = new Map<string, JobDisplayStatus>()
    jobs.forEach((job) => {
      const fresh = getJobDisplayStatus(job, now, timeZone)
      const cached = cacheRef.current.get(job.id)
      next.set(
        job.id,
        cached && cached.kind === fresh.kind && cached.label === fresh.label && cached.detail === fresh.detail ? cached : fresh,
      )
    })
    cacheRef.current = next
    return next
  }, [jobs, now, timeZone])
}

const EMPTY_CANDIDATES: NearbyCandidate[] = []

const dateKeyToLocalDate = (dateKey: string) => {
  const [year, month, day] = dateKey.split('-').map(Number)
  return new Date(year, month - 1, day, 12)
}

const localDateToDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

/** Board job ids are the backend job id with this prefix (see realtime-api JOB_PREFIX). */
const FOCUS_BOARD_JOB_PREFIX = 'job:'
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
/** Tab a focused job opens on: the first list that contains it. */
const FOCUS_TAB_ORDER: JobsTab[] = ['unassigned', 'late', 'today']

type FocusJobLookup = { status: 'loading' } | { status: 'found'; job: ApiJob } | { status: 'not_found' }

interface RealTimePageProps {
  serviceInstanceId: string | null
  /** Focused mode (?job_id=): only this job is shown. Absent = the normal board. */
  focusJobId?: string | null
  /** YYYY-MM-DD the focused job is scheduled on; the board opens on that day. */
  focusDate?: string | null
  onShowAllJobs?: () => void
  onBackToJobs?: () => void
}

export function RealTimePage({ serviceInstanceId, focusJobId = null, focusDate = null, onShowAllJobs, onBackToJobs }: RealTimePageProps) {
  const source = useMemo(
    () => (realtimeConfig.useMock ? createMockRealtimeSource() : createApiRealtimeSource({ serviceInstanceId })),
    [serviceInstanceId],
  )
  const appTimeZone = useOrganizationTimeZone(serviceInstanceId)
  const timeZone = source.kind === 'mock' ? MOCK_TIME_ZONE : appTimeZone

  // Minute-level clock for relative labels, lateness and the timeline "now" line.
  const now = useNow(30_000)
  const todayKey = toDateKey(now, timeZone)
  const [dateKey, setDateKey] = useState(() => (focusJobId && focusDate && DATE_KEY_PATTERN.test(focusDate) ? focusDate : todayKey))
  const isToday = dateKey === todayKey

  const sync = useRealtimeSocket({ source, dateKey, timeZone })
  // After the admin's own Assign / Remove, re-read assignments right away (not at the max age).
  const actions = useJobWorkerActions(source, { onStaffingChanged: sync.refreshStaffing })
  const canForceRemove = useAuthStore((state) => hasPostPermission(state.currentPost as never, schedulingRules.forceRemovePermission))
  const workers = useWorkers()
  const jobs = useJobs()
  const highlightedJobs = useHighlightedJobs()
  const reducedMotion = usePrefersReducedMotion()
  const isMobile = useMediaQuery('(max-width: 767px)')

  const [kpiFilter, setKpiFilter] = useState<KpiFilter | null>(null)
  const [jobsTab, setJobsTab] = useState<JobsTab>('unassigned')
  const [layers, setLayers] = useState<Record<MapLayer, boolean>>({ workers: true, jobs: true, routes: true })
  // Selection lives in the shared store so the jobs panel, map and timeline stay in sync.
  const selectedJobId = useRealtimeStore((state) => state.selectedJobId)
  const selectedWorkerId = useRealtimeStore((state) => state.selectedWorkerId)
  const toggleJobSelection = useRealtimeStore((state) => state.toggleJobSelection)
  const setSelectedJob = useRealtimeStore((state) => state.setSelectedJob)
  const selectWorker = useRealtimeStore((state) => state.selectWorker)
  const clearSelection = useRealtimeStore((state) => state.clearSelection)
  const selectionNotice = useRealtimeStore((state) => state.selectionNotice)
  const detachedJob = useRealtimeStore((state) => state.detachedJob)
  const announcement = useRealtimeStore((state) => state.announcement)
  const hoveredWorkerId = useRealtimeStore((state) => state.hoveredWorkerId)
  const focusedCandidate = useRealtimeStore((state) => state.focusedCandidate)
  const setHoveredWorker = useRealtimeStore((state) => state.setHoveredWorker)
  // Row flashes for the focused card only; the normal board does not subscribe (JobsPanel does).
  const focusFlashedRows = useRealtimeStore((state) => (focusJobId ? state.flashedRows : undefined))
  const [radiusKm, setRadiusKm] = useState(NEARBY_RADIUS_KM)
  const [candidateLimit, setCandidateLimit] = useState(MAX_NEARBY_CANDIDATES)
  /** Kept across selections, so a minimized popup stays out of the way. */
  const [popupMinimized, setPopupMinimized] = useState(false)
  const [removeRequest, setRemoveRequest] = useState<RemoveWorkerRequest | null>(null)
  const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null)
  const [draggingJobId, setDraggingJobId] = useState<string | null>(null)
  const [sheetSnap, setSheetSnap] = useState<SheetSnap>('peek')
  const [mapElement, setMapElement] = useState<HTMLDivElement | null>(null)
  const [sheetOverlap, setSheetOverlap] = useState(0)
  const [datePickerOpen, setDatePickerOpen] = useState(false)

  const isLoading = sync.isInitialLoading
  // First paint comes from Job/Realtime + Job/List; assignments (who is on which job) follow.
  const staffingLoaded = useRealtimeStore((state) => state.staffingLoaded)
  const staffingLoading = !isLoading && !staffingLoaded
  const displayById = useStableDisplayMap(jobs, now, timeZone)
  const jobsById = useMemo(() => new Map(jobs.map((job) => [job.id, job])), [jobs])
  const workersById = useMemo(() => Object.fromEntries(workers.map((worker) => [worker.id, worker])), [workers])

  // Focused mode: the job itself comes from Job/Get (it may be past or completed), the card and
  // staffing from the board for its day.
  const [focusLookup, setFocusLookup] = useState<FocusJobLookup>({ status: 'loading' })
  useEffect(() => {
    if (!focusJobId) return undefined
    const controller = new AbortController()
    void jobsApi.getJob(focusJobId, { signal: controller.signal }).then(
      (job) => setFocusLookup({ status: 'found', job }),
      () => {
        if (!controller.signal.aborted) setFocusLookup({ status: 'not_found' })
      },
    )
    return () => controller.abort()
  }, [focusJobId])
  const focusApiJob = focusLookup.status === 'found' ? focusLookup.job : null
  const focusBoardJob = focusJobId ? jobsById.get(`${FOCUS_BOARD_JOB_PREFIX}${focusJobId}`) ?? null : null
  const focusHasLocation = Boolean(focusApiJob && toValidCoords(focusApiJob.latitude, focusApiJob.longitude))
  // Found, but no worker slots on this day: map pin only (no card, nearby workers or Assign).
  const focusOffBoard = Boolean(focusApiJob) && staffingLoaded && !focusBoardJob
  const focusPinJob = useMemo(() => {
    if (!focusApiJob || !focusHasLocation) return null
    return buildPendingJob(focusApiJob, {
      coords: toValidCoords(focusApiJob.latitude, focusApiJob.longitude),
      address: focusApiJob.address ?? null,
      area: focusApiJob.location ?? null,
      categoryId: null,
      categoryName: null,
    })
  }, [focusApiJob, focusHasLocation])

  // Jobs on the selected day, plus overdue unassigned bookings from earlier days.
  const boardJobs = useMemo(
    () => jobs.filter((job) => isJobOnDate(job, dateKey, timeZone) || displayById.get(job.id)?.kind === 'overdue'),
    [dateKey, displayById, jobs, timeZone],
  )
  const dayJobs = useMemo(() => boardJobs.filter((job) => isJobOnDate(job, dateKey, timeZone)), [boardJobs, dateKey, timeZone])

  const kpis = useMemo(
    () => (isLoading ? null : computeKpis(workers, boardJobs, now, timeZone)),
    [boardJobs, isLoading, now, timeZone, workers],
  )

  const filtered = useMemo(
    () => applyKpiFilter(workers, boardJobs, kpiFilter, now, timeZone),
    [boardJobs, kpiFilter, now, timeZone, workers],
  )

  // Focused mode shows only the focused job (panel and map); otherwise the filtered board.
  const visibleJobs = useMemo(
    () => (focusJobId ? (focusBoardJob ? [focusBoardJob] : []) : filtered.jobs),
    [filtered.jobs, focusBoardJob, focusJobId],
  )

  const lists = useMemo<Record<JobsTab, Job[]>>(() => {
    // The selected job stays listed while selected; a job another admin removed keeps its banner.
    const pinnedJob = selectedJobId
      ? visibleJobs.find((job) => job.id === selectedJobId) ?? (selectionNotice?.kind === 'deleted' ? detachedJob : null)
      : null
    return buildJobLists(visibleJobs, { now, dateKey, timeZone, displayById, pinnedJob })
  }, [dateKey, detachedJob, displayById, visibleJobs, now, selectedJobId, selectionNotice, timeZone])

  const jobNumbers = useMemo(() => {
    const numbers = new Map<string, number>()
    ;[...dayJobs, ...boardJobs.filter((job) => !dayJobs.includes(job))].forEach((job, index) => numbers.set(job.id, index + 1))
    return numbers
  }, [boardJobs, dayJobs])


  // Same rule as the nearby strip (category, availability, 10 km), for "Assign · N nearby".
  const nearbyCountById = useMemo(() => {
    const counts = new Map<string, number>()
    boardJobs.forEach((job) => {
      if (isJobUnderstaffed(job, now)) counts.set(job.id, countNearbyCandidates(job, workers))
    })
    return counts
  }, [boardJobs, now, workers])

  const selectedJob = selectedJobId
    ? jobsById.get(selectedJobId) ?? (detachedJob?.id === selectedJobId ? detachedJob : null)
    : null
  const selectedDetached = Boolean(selectedJob) && selectionNotice?.kind === 'deleted' && selectionNotice.jobId === selectedJobId
  const canAddToSelected = Boolean(selectedJob) && !selectedDetached && selectedJob?.status !== 'completed'

  const nearby = useNearbyCandidates({
    source,
    job: selectedJob,
    workers,
    jobs,
    radiusKm,
    enabled: canAddToSelected && !selectedJob?.staffingPending,
  })
  const visibleCandidates = useMemo(() => nearby.candidates.slice(0, candidateLimit), [candidateLimit, nearby.candidates])

  const timelineWorkers = useMemo(
    () => [...filtered.workers].sort((a, b) => WORKER_ORDER[a.status] - WORKER_ORDER[b.status] || a.name.localeCompare(b.name)),
    [filtered.workers],
  )
  const timelineJobs = useMemo(() => filtered.jobs.filter((job) => isJobOnDate(job, dateKey, timeZone)), [dateKey, filtered.jobs, timeZone])

  const onlineCount = useMemo(() => workers.filter((worker) => worker.status !== 'offline').length, [workers])
  const workersWithoutLocation = useMemo(() => filtered.workers.filter((worker) => !hasValidCoords(worker)).length, [filtered.workers])

  // The store is a singleton; clear it so another branch never flashes this branch's data.
  useEffect(() => () => useRealtimeStore.getState().reset(), [])

  // Every newly selected job starts from the default radius and list length.
  useEffect(() => {
    setRadiusKm(NEARBY_RADIUS_KM)
    setCandidateLimit(MAX_NEARBY_CANDIDATES)
  }, [selectedJobId])

  // Focused mode: once staffing is known, open the job's tab and select it (popup, nearby workers).
  const focusAppliedRef = useRef(false)
  useEffect(() => {
    if (!focusBoardJob || !staffingLoaded || focusAppliedRef.current) return
    focusAppliedRef.current = true
    const tab = FOCUS_TAB_ORDER.find((key) => lists[key].some((job) => job.id === focusBoardJob.id))
    if (tab) setJobsTab(tab)
    setSelectedJob(focusBoardJob.id)
  }, [focusBoardJob, lists, setSelectedJob, staffingLoaded])

  // Esc clears the selection (worker card first, then the job) unless a dialog/sheet owns Esc.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"]')) return
      const { selectedWorkerId: workerId, selectedJobId: jobId } = useRealtimeStore.getState()
      if (workerId) selectWorker(null)
      else if (jobId) clearSelection()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [clearSelection, selectWorker])

  const handleToggleKpi = useCallback((filter: KpiFilter) => {
    setKpiFilter((current) => {
      const next = current === filter ? null : filter
      if (next) setJobsTab(KPI_TO_TAB[next])
      return next
    })
  }, [])

  /** Mobile: bring the map to the top of the viewport so the sheet sits over it. */
  const revealMapBehindSheet = useCallback(() => {
    mapElement?.scrollIntoView({ block: 'start', behavior: reducedMotion ? 'auto' : 'smooth' })
  }, [mapElement, reducedMotion])

  /** Select / clear a job (only one at a time). */
  const handleSelectJob = useCallback((jobId: string) => {
    const willSelect = useRealtimeStore.getState().selectedJobId !== jobId
    toggleJobSelection(jobId)
    if (willSelect && isMobile) {
      setSheetSnap((current) => (current === 'peek' ? 'half' : current))
      revealMapBehindSheet()
    }
  }, [isMobile, revealMapBehindSheet, toggleJobSelection])

  /** "Assign" on a collapsed card: select it so the nearby strip and candidate pins appear. */
  const handleOpenAssign = useCallback((jobId: string) => {
    setSelectedJob(jobId)
    if (isMobile) {
      setSheetSnap((current) => (current === 'peek' ? 'half' : current))
      revealMapBehindSheet()
    }
  }, [isMobile, revealMapBehindSheet, setSelectedJob])

  const handleDismissNotice = useCallback(() => {
    const { selectionNotice: notice } = useRealtimeStore.getState()
    if (notice?.kind === 'deleted') clearSelection()
    else useRealtimeStore.getState().setSelectionNotice(null)
  }, [clearSelection])

  /** Remove from the card or popup: blocked jobs say why; otherwise confirm first. */
  const handleRemoveRequest = useCallback((jobId: string, workerId: string) => {
    const state = useRealtimeStore.getState()
    const job = state.jobsById[jobId] ?? (state.detachedJob?.id === jobId ? state.detachedJob : null)
    const jobWorker = job?.workers.find((entry) => entry.workerId === workerId)
    if (!job || !jobWorker) return
    const rule = getRemovalRule(job, jobWorker, { canForceRemove })
    if (!rule.allowed) {
      toast.error(rule.blockedReason ?? 'This worker cannot be removed')
      return
    }
    setRemoveRequest({ job, worker: state.workersById[workerId] ?? null, jobWorker, rule })
  }, [canForceRemove])

  const handleRemoveConfirm = useCallback(({ reason, note }: { reason: RemovalReason | null; note: string | null }) => {
    if (!removeRequest) return
    const { job, jobWorker } = removeRequest
    setRemoveRequest(null)
    const latest = useRealtimeStore.getState().jobsById[job.id] ?? job
    const wasLast = isActiveResponse(getEffectiveResponse(jobWorker, Date.now())) && getActiveJobWorkers(latest).length === 1
    void actions.removeWorker(latest, jobWorker.workerId, { reason, note })
    // The job is back to unassigned: show it there, still selected, so someone can be added
    // straight from the nearby strip.
    if (wasLast) setJobsTab('unassigned')
  }, [actions, removeRequest])

  /** Add from the nearby strip: conflicts ask first, everyone else is added straight away. */
  const handleAddCandidate = useCallback((candidate: NearbyCandidate) => {
    const job = selectedJobId ? useRealtimeStore.getState().jobsById[selectedJobId] : null
    if (!job) return
    if (candidate.hasConflict) {
      setPendingDrop({ job, worker: candidate.worker, conflicts: candidate.conflicts, hasConflict: true })
      return
    }
    void actions.addWorker(job, candidate.worker.id)
  }, [actions, selectedJobId])

  const handleIncreaseRadius = useCallback(() => {
    setRadiusKm((current) => Math.min(current + NEARBY_RADIUS_STEP_KM, NEARBY_MAX_RADIUS_KM))
  }, [])
  const handleShowMore = useCallback(() => setCandidateLimit((current) => current + MAX_NEARBY_CANDIDATES), [])

  const hasSelection = Boolean(selectedJob)
  const fitPadding = useMemo(
    () => computeFitPadding({ isMobile, hasSelection, popupExpanded: hasSelection && !popupMinimized, sheetOverlapPx: sheetOverlap }),
    [hasSelection, isMobile, popupMinimized, sheetOverlap],
  )

  const handleToggleLayer = useCallback((layer: MapLayer) => {
    setLayers((current) => ({ ...current, [layer]: !current[layer] }))
  }, [])

  const handleDateChange = (next: string) => {
    setDateKey(next)
    clearSelection()
    setDatePickerOpen(false)
  }

  // Drag & drop: job card -> worker row, validated then confirmed.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggingJobId((event.active.data.current?.jobId as string | undefined) ?? null)
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setDraggingJobId(null)
    const jobId = event.active.data.current?.jobId as string | undefined
    const workerId = event.over?.data.current?.workerId as string | undefined
    if (!jobId || !workerId) return
    const job = jobsById.get(jobId)
    const worker = workersById[workerId]
    if (!job || !worker) return
    if (jobHasWorker(job, workerId)) {
      toast.info(`${worker.name} is already on ${job.bookingNo}`)
      return
    }
    const conflicts = findWorkerConflicts(job, workerId, jobs)
    setPendingDrop({ job, worker, conflicts, hasConflict: conflicts.length > 0 })
  }, [jobs, jobsById, workersById])

  const draggingJob = draggingJobId ? jobsById.get(draggingJobId) ?? null : null
  const dateLabel = formatDateKeyLabel(dateKey)
  const tzLabel = getTimeZoneAbbreviation(timeZone, now)
  const fitKey = `${serviceInstanceId ?? 'none'}|${dateKey}`
  const showFatalError = Boolean(sync.error) && !isLoading && workers.length === 0 && jobs.length === 0

  const focusDisplay = focusBoardJob ? displayById.get(focusBoardJob.id) : undefined
  const focusJobNumber = focusBoardJob?.bookingNo ?? (focusApiJob ? focusApiJob.job_ref || `#${focusApiJob.id}` : null)
  const jobsPanel = focusJobId ? (
    // Focused mode: one job under a "Job <number>" header instead of the tab bar.
    <section aria-label="Job" className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-[#dfeae3] bg-white">
      <div className="flex min-h-[45px] items-center justify-between gap-2 border-b border-[#dfeae3] px-3 py-2">
        <span className="truncate text-[13px] font-semibold text-[#20352b]">{focusJobNumber ? `Job ${focusJobNumber}` : 'Job'}</span>
        {focusDisplay ? <StatusChip kind={focusDisplay.kind} label={focusDisplay.label} /> : null}
      </div>
      {focusBoardJob && focusDisplay ? (
        <div role="list" className="min-h-0 flex-1 overflow-y-auto p-2">
          <JobCard
            job={focusBoardJob}
            display={focusDisplay}
            workersById={workersById}
            nearbyCount={nearbyCountById.get(focusBoardJob.id) ?? 0}
            isSelected={selectedJobId === focusBoardJob.id}
            highlightToken={highlightedJobs[focusBoardJob.id] ?? 0}
            timeZone={timeZone}
            now={now}
            canForceRemove={canForceRemove}
            hoveredWorkerId={selectedJobId === focusBoardJob.id ? hoveredWorkerId : null}
            flashedRows={selectedJobId === focusBoardJob.id ? focusFlashedRows : undefined}
            notice={selectedJobId === focusBoardJob.id && selectionNotice?.jobId === focusBoardJob.id ? selectionNotice.kind : null}
            onDismissNotice={handleDismissNotice}
            onToggle={handleSelectJob}
            onAssign={handleOpenAssign}
            onRemoveWorker={handleRemoveRequest}
            onHoverWorker={setHoveredWorker}
          />
        </div>
      ) : focusOffBoard || focusLookup.status === 'not_found' ? (
        <div className="flex flex-1 items-center justify-center p-4 text-center text-xs text-[#718078]">
          {focusOffBoard ? 'No worker slots for this job on this day.' : 'Job not found'}
        </div>
      ) : (
        <div className="p-2" aria-busy="true" aria-label="Loading job">
          <Skeleton className="h-[92px] w-full rounded-lg" />
        </div>
      )}
    </section>
  ) : (
    <JobsPanel
      tab={jobsTab}
      onTabChange={setJobsTab}
      lists={lists}
      workersById={workersById}
      displayById={displayById}
      nearbyCountById={nearbyCountById}
      selectedJobId={selectedJobId}
      highlightedJobs={highlightedJobs}
      timeZone={timeZone}
      now={now}
      isLoading={isLoading}
      staffingLoading={staffingLoading}
      canForceRemove={canForceRemove}
      selectionNotice={selectionNotice}
      onDismissNotice={handleDismissNotice}
      onToggle={handleSelectJob}
      onAssign={handleOpenAssign}
      onRemoveWorker={handleRemoveRequest}
      className="h-full"
    />
  )

  const nearbyStrip = selectedJob && canAddToSelected ? (
    <NearbyWorkersStrip
      key={selectedJob.id}
      jobId={selectedJob.id}
      categoryName={selectedJob.categoryName ?? null}
      status={selectedJob.staffingPending ? 'loading' : nearby.status}
      candidates={visibleCandidates}
      totalCount={nearby.candidates.length}
      jobLocated={nearby.jobLocated}
      radiusKm={radiusKm}
      canIncreaseRadius={radiusKm < NEARBY_MAX_RADIUS_KM}
      hoveredWorkerId={hoveredWorkerId}
      focusedCandidate={focusedCandidate}
      variant={isMobile ? 'inline' : 'overlay'}
      onAdd={handleAddCandidate}
      onHover={setHoveredWorker}
      onRetry={nearby.retry}
      onIncreaseRadius={handleIncreaseRadius}
      onShowMore={handleShowMore}
    />
  ) : null

  // Desktop / tablet only: on mobile the bottom sheet card is the job summary.
  const mapOverlays = !isMobile && selectedJob ? (
    <>
      <MapJobPopup
        job={selectedJob}
        display={displayById.get(selectedJob.id)}
        workersById={workersById}
        timeZone={timeZone}
        now={now}
        canForceRemove={canForceRemove && !selectedDetached}
        minimized={popupMinimized}
        hoveredWorkerId={hoveredWorkerId}
        onMinimizedChange={setPopupMinimized}
        onClose={clearSelection}
        onRemoveWorker={handleRemoveRequest}
        onHoverWorker={setHoveredWorker}
      />
      {nearbyStrip}
    </>
  ) : null

  return (
    <DndContext sensors={sensors} collisionDetection={collisionDetection} measuring={DND_MEASURING} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setDraggingJobId(null)}>
      <div className={cn('min-w-0 space-y-4', isMobile && 'pb-[32vh]')}>
        {/* Announces live changes to the expanded job (worker responses, status) for screen readers. */}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {announcement ? <span key={announcement.id}>{announcement.text}</span> : null}
        </div>

        {/* Header */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-[#20352b] sm:text-[28px]">Real-time worker scheduling</h1>
            <p className="mt-0.5 text-[13px] text-[#718078]">
              {focusJobId ? 'Job details' : isToday ? 'Live operations for today' : 'Schedule for the selected day'} · {dateLabel} · {tzLabel}
              {source.kind === 'mock' ? <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">Demo data</span> : null}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <LiveStatusPill connection={sync.connection} isPaused={sync.isPaused} lastUpdatedAt={sync.lastUpdatedAt} onRetry={sync.retry} />
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 text-xs" aria-label={`Change day, currently ${isToday && !focusJobId ? 'today' : dateLabel}`}>
                  <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                  {isToday && !focusJobId ? 'Today' : dateLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dateKeyToLocalDate(dateKey)}
                  onSelect={(date) => date && handleDateChange(localDateToDateKey(date))}
                />
                {!isToday ? (
                  <div className="border-t p-2">
                    <Button type="button" size="sm" variant="ghost" className="w-full text-xs" onClick={() => handleDateChange(todayKey)}>
                      Back to today
                    </Button>
                  </div>
                ) : null}
              </PopoverContent>
            </Popover>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={sync.refresh}
              disabled={sync.isRefreshing || isLoading}
              aria-label="Refresh now"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', (sync.isRefreshing || isLoading) && 'motion-safe:animate-spin')} aria-hidden="true" />
            </Button>
          </div>
        </div>

        {focusJobId ? (
          focusLookup.status === 'not_found' ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              <span className="font-semibold">Job not found</span>
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={onShowAllJobs}>
                Show all jobs
              </Button>
            </div>
          ) : (
            <div role="status" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#cfe6d8] bg-[#f3faf6] px-3 py-2 text-xs text-[#20352b]">
              <div className="min-w-0 space-y-0.5">
                {focusApiJob ? (
                  <p>
                    <strong>Showing job {focusApiJob.job_ref || `#${focusApiJob.id}`}</strong>
                    {focusApiJob.title ? ` · ${focusApiJob.title}` : ''}
                    {focusApiJob.scheduled_start
                      ? ` · ${formatDateKeyLabel(toDateKey(focusApiJob.scheduled_start, timeZone))} · ${focusApiJob.scheduled_end
                        ? formatTimeRangeInZone(focusApiJob.scheduled_start, focusApiJob.scheduled_end, timeZone)
                        : formatTimeInZone(focusApiJob.scheduled_start, timeZone)} ${tzLabel}`
                      : ''}
                  </p>
                ) : (
                  <p>Loading job…</p>
                )}
                {focusApiJob && !focusHasLocation ? <p className="text-amber-800">This job has no location</p> : null}
              </div>
              <div className="flex items-center gap-2">
                {onBackToJobs ? (
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={onBackToJobs}>
                    Back to jobs
                  </Button>
                ) : null}
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={onShowAllJobs}>
                  Show all jobs
                </Button>
              </div>
            </div>
          )
        ) : null}

        {showFatalError ? (
          <div role="alert" className="flex flex-col items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-8 text-center">
            <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
            <p className="text-sm font-semibold text-red-800">Unable to load realtime operations</p>
            <p className="text-xs text-red-700">{sync.error?.message}</p>
            <Button type="button" size="sm" variant="outline" onClick={sync.retry} className="mt-1 gap-1.5 border-red-200 text-red-700 hover:bg-red-100">
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Retry
            </Button>
          </div>
        ) : (
          <>
            {!focusJobId ? (
              <KpiCards counts={kpis} staffingLoading={staffingLoading} activeFilter={kpiFilter} onToggle={handleToggleKpi} />
            ) : null}

            {kpiFilter ? (
              <div className="flex items-center gap-2 text-xs text-[#355445]" role="status">
                <span>
                  Showing <strong>{KPI_LABEL[kpiFilter]}</strong> across jobs, map and schedule.
                </span>
                <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => setKpiFilter(null)}>
                  <X className="h-3 w-3" aria-hidden="true" />
                  Clear filter
                </Button>
              </div>
            ) : null}

            {/* Main row: compact jobs panel (232px, own scroll) beside the map; map above the list on
                tablet, list in a bottom sheet on mobile. */}
            <div className="flex flex-col gap-4 lg:grid lg:h-[clamp(520px,calc(100vh-260px),760px)] lg:grid-cols-[232px_minmax(0,1fr)]">
              {!isMobile ? <div className="order-2 max-h-[560px] min-h-[360px] lg:order-1 lg:h-full lg:max-h-none">{jobsPanel}</div> : null}
              {/* Always mounted (even while the map loads): the mobile sheet measures and scrolls to it. */}
              <div ref={setMapElement} className="order-1 h-[60vh] min-h-[320px] scroll-mt-24 md:h-[400px] lg:sticky lg:top-4 lg:order-2 lg:h-full">
                <WorkersMap
                  serviceInstanceId={serviceInstanceId}
                  workers={filtered.workers}
                  allWorkers={workers}
                  jobs={focusOffBoard && focusPinJob ? [focusPinJob] : visibleJobs}
                  selectedJob={selectedJob ?? (focusOffBoard ? focusPinJob : null)}
                  candidates={canAddToSelected ? visibleCandidates : EMPTY_CANDIDATES}
                  candidatesSettled={nearby.status !== 'loading'}
                  overlays={mapOverlays}
                  hasBottomOverlay={Boolean(mapOverlays && nearbyStrip)}
                  selectedWorkerId={selectedWorkerId}
                  jobNumbers={jobNumbers}
                  displayById={displayById}
                  layers={layers}
                  onToggleLayer={handleToggleLayer}
                  onSelectJob={handleSelectJob}
                  onFocusJob={setSelectedJob}
                  onSelectWorker={selectWorker}
                  onClearSelection={clearSelection}
                  onlineCount={onlineCount}
                  workersWithoutLocation={workersWithoutLocation}
                  fitKey={fitKey}
                  fitPadding={fitPadding}
                  isLoading={isLoading}
                  reducedMotion={reducedMotion}
                  now={now}
                  focusMode={Boolean(focusJobId)}
                  className="h-full"
                />
              </div>
            </div>

            {/* Focused mode is one job: no day schedule. */}
            {!focusJobId ? (
              <LiveScheduleTimeline
                workers={timelineWorkers}
                jobs={timelineJobs}
                dateKey={dateKey}
                dateLabel={dateLabel}
                timeZone={timeZone}
                timeZoneLabel={tzLabel}
                workHours={realtimeConfig.workHours}
                now={now}
                isToday={isToday}
                displayById={displayById}
                selectedJobId={selectedJobId}
                isLoading={isLoading || staffingLoading}
                emptyTitle={kpiFilter ? 'No workers match this filter' : 'No workers on the schedule'}
                emptyDescription={kpiFilter
                  ? 'Clear the filter to see every worker for this day.'
                  : 'Workers appear here once they are added to this branch. Unassigned jobs can still be assigned from the jobs list.'}
                onSelectJob={handleSelectJob}
              />
            ) : null}
          </>
        )}

        {isMobile && !showFatalError ? (
          <MobileJobsSheet
            snap={sheetSnap}
            onSnapChange={setSheetSnap}
            mapElement={mapElement}
            onMapOverlapChange={setSheetOverlap}
            title={`Jobs · ${lists.unassigned.length} unassigned${lists.late.length ? ` · ${lists.late.length} late` : ''}`}
          >
            <div className="flex h-full flex-col gap-2">
              {nearbyStrip}
              <div className="min-h-0 flex-1">{jobsPanel}</div>
            </div>
          </MobileJobsSheet>
        ) : null}

        <AlertDialog open={Boolean(pendingDrop)} onOpenChange={(open) => !open && setPendingDrop(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Add {pendingDrop?.worker.name} to {pendingDrop?.job.bookingNo}?
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm">
                  {pendingDrop ? (
                    <p>
                      {pendingDrop.job.service} · {formatTimeRangeInZone(pendingDrop.job.scheduledStart, pendingDrop.job.scheduledEnd, timeZone)} {tzLabel}
                      {pendingDrop.job.area ? ` · ${pendingDrop.job.area}` : ''}
                    </p>
                  ) : null}
                  {pendingDrop?.hasConflict ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                      <p className="flex items-center gap-1.5 font-semibold">
                        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                        Schedule conflict
                      </p>
                      {pendingDrop.conflicts.length > 0 ? (
                        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                          {pendingDrop.conflicts.map((conflict) => (
                            <li key={conflict.id}>
                              Overlaps {conflict.bookingNo} {conflict.service} ({formatTimeRangeInZone(conflict.scheduledStart, conflict.scheduledEnd, timeZone)})
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-1 text-xs">{pendingDrop.worker.name} already has a job at this time.</p>
                      )}
                    </div>
                  ) : null}
                  {pendingDrop?.worker.status === 'offline' ? (
                    <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                      {pendingDrop.worker.name} is currently offline and may not see this assignment straight away.
                    </p>
                  ) : null}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={cn(pendingDrop?.hasConflict ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#169b62] hover:bg-[#118753]')}
                onClick={() => {
                  if (pendingDrop) void actions.addWorker(pendingDrop.job, pendingDrop.worker.id)
                  setPendingDrop(null)
                }}
              >
                {pendingDrop?.hasConflict ? 'Add anyway' : 'Add worker'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <RemoveWorkerDialog request={removeRequest} onCancel={() => setRemoveRequest(null)} onConfirm={handleRemoveConfirm} />
      </div>

      <DragOverlay dropAnimation={reducedMotion ? null : undefined}>
        {draggingJob ? (
          <div className="w-64 rounded-lg border border-[#169b62] bg-white p-3 shadow-xl">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13px] font-semibold text-[#20352b]">{draggingJob.bookingNo}</span>
              {displayById.get(draggingJob.id) ? (
                <StatusChip kind={(displayById.get(draggingJob.id) as JobDisplayStatus).kind} label={(displayById.get(draggingJob.id) as JobDisplayStatus).label} />
              ) : null}
            </div>
            <p className="mt-1 text-[12px] text-[#4e6157]">{draggingJob.service}</p>
            <p className="mt-1 text-[11px] text-[#718078]">{formatTimeRangeInZone(draggingJob.scheduledStart, draggingJob.scheduledEnd, timeZone)}</p>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
